import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReportDataService from '../../services/report.service';
import { useTranslation } from '../../hooks/useTranslation';
import { formatCurrencySync } from '../../utils/currencyUtils';

const SalesByProductDashboard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      
      const response = await ReportDataService.getSalesByProduct(params);
      if (response.data && response.data.data) {
        setData(response.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || t('reports.errorLoading'));
      console.error('Error loading sales by product:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadData();
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setTimeout(loadData, 100);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <BarChartIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h5" component="h2">
              {t('reports.salesByProduct')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {t('reports.salesByProductDescription')}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('reports.startDate')}
                  value={startDate}
                  onChange={setStartDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t('reports.endDate')}
                  value={endDate}
                  onChange={setEndDate}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" gap={1}>
                <Button variant="contained" onClick={handleFilter} startIcon={<SearchIcon />}>
                  {t('reports.filter')}
                </Button>
                <Button variant="outlined" onClick={handleReset}>
                  {t('reports.reset')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>{t('reports.product')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.totalSales')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.totalQuantity')}</strong></TableCell>
                  <TableCell align="right"><strong>{t('reports.saleCount')}</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.noData')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((item) => (
                    <TableRow key={item.product_id} hover>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell align="right">
                        {formatCurrencySync(item.total_sales || 0)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(item.total_quantity || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{item.sale_count || 0}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SalesByProductDashboard;

